// SaleOrderDetail.tsx
import React, { useEffect, useState, useRef } from "react";
import ReactDOM from "react-dom";
import { useNavigate, useParams } from "react-router-dom";
import {
  FaCubes,
  FaCheckCircle,
  FaShippingFast,
  FaSpinner,
  FaClipboardCheck,
  FaFileUpload,
  FaEllipsisV,
} from "react-icons/fa";
import { useSelector } from "react-redux";
import WebService from "../Services/WebService";
import { mediaUrl } from "../Services/mediaUrl";
import type { RootState } from "../Services/Store";
import styles from "./SaleOrderDetail.module.css";
import CapsuleTabs from "./CapsuleTabs";
import AcquireRawMaterialPanel from "./AcquireRawMaterialPanel";
import Swal from "sweetalert2";
import { toast } from "react-toastify";
import GenerateInvoicePanel from "./GenerateInvoicePanel";
import EditSaleOrderPanel from "./EditSaleOrderPanel";
import BillingDocuments from "./BillingDocuments";
import { downloadInvoicePdf, hasInvoiceDoc, invoiceIdOf } from "../Services/billing";
import AddSaleOrderPanel from "./AddSaleOrderPanel";
import salesStyles from "./Sales.module.css";
import BulkConfirmModal, { BulkAction, isEstimateStatus, isPlacedStatus } from "./BulkConfirmModal";

interface ClientOpenOrder {
  sale_order_id: number;
  status: string;
  final_amount?: number | string;
  created_at?: string;
}

// ----- Types -----
interface SaleOrderItem {
  sale_order_items_id: number;
  product_type: "PRODUCT" | "RAW_MATERIAL";
  product_id: number;
  product_name?: string;
  name?: string;
  product?: { name?: string };

  quantity: number;
  height: number;
  width: number;
  unit_price?: number;
  discount?: number;
  total_price: number;

  // Additional attributes we want to display
  height_margin?: number;
  width_margin?: number;
  mica_id?: number;
  color?: string;
  door_config?: boolean;
  is_back_design?: boolean;
  is_sagwan_bidding?: boolean;
  is_back_puc?: boolean; // "Back PVC"
  wood_quality_id?: number;

  [key: string]: any;
}

interface Invoice {
  id?: number;
  invoice_id?: number;
  invoice_number?: string;
  sale_order_id: number;
  invoice_date: string;
  discount_amount: number;
  total_amount: number;
  grand_total?: number;
  status?: string;
  notes: string;
  created_at: string;
  created_by?: number;
  updated_by?: number;
  updated_at?: string;
  deleted_at?: string | null;
}

interface ReceiptSummary {
  receipt_id?: number;
  receipt_number?: string;
  amount?: number;
  payment_method?: string;
  paid_at?: string | null;
  status?: string;
  pdf_path?: string;
}

interface OrderData {
  sale_order_id: number;
  customer_id: number;
  user_id: number;
  username: string;
  status: string;
  shipping_address: number;
  notes?: string;
  total_amount: number;
  discount_amount: number;
  tax_amount: number;
  final_amount: number;
  order_date?: string;
  created_at?: string;
  sale_order_items: SaleOrderItem[];
  expected_delivery_date?: string | null;
  production_order_generated?: boolean;
  lr_document?: string | null;
  invoice: Invoice | null;
  invoice_id?: number | null;
  receipts?: ReceiptSummary[];
  has_product_items?: boolean;
  has_raw_material_items?: boolean;
  requires_raw_material_acquisition?: boolean;
  raw_materials_acquired?: boolean;
  acquired_raw_material_count?: number;
}

function orderRequiresRawMaterialAcquisition(order?: OrderData | null): boolean {
  if (!order) return false;
  if (typeof order.requires_raw_material_acquisition === "boolean") {
    return order.requires_raw_material_acquisition;
  }
  return (order.sale_order_items || []).some(
    (item) => (item.product_type || "PRODUCT").toUpperCase() !== "RAW_MATERIAL"
  );
}

interface AcquiredRawMaterial {
  acquisition_id: number;
  raw_material_id: number;
  raw_material_name: string;
  quantity: number;
}

// ---- Status steps & labels ----
const ORDER_STATUS_STEPS = [
  { key: "PENDING", label: "Pending" },
  { key: "IN_PROCESSING", label: "In Processing" },
  { key: "SHIPPED", label: "Shipped" },
  { key: "COMPLETED", label: "Completed" },
] as const;

const STEP_ICONS: Record<string, React.ReactNode> = {
  PENDING: <FaSpinner />,
  IN_PROCESSING: <FaCubes />,
  SHIPPED: <FaShippingFast />,
  COMPLETED: <FaClipboardCheck />,
};
const now = new Date();

let hours = now.getHours();
const ampm = hours >= 12 ? "PM" : "AM";
hours = hours % 12 || 12;

// Windows-safe filename (no ':' characters)
const timestamp = `${now.getFullYear()}-${String(
  now.getMonth() + 1
).padStart(2, "0")}-${String(now.getDate()).padStart(2, "0")}_${String(
  hours
).padStart(2, "0")}_${String(now.getMinutes()).padStart(2, "0")} ${ampm}`;


// (visual-only flag to keep old buttons hidden but code preserved)
const SHOW_LEGACY_ACTION_BTNS = false;

const SaleOrderDetail: React.FC = () => {
  const { orderId } = useParams<{ orderId: string }>();
  const user = useSelector((state: RootState) => state.auth.user);
  const navigate = useNavigate();

  const [orderData, setOrderData] = useState<OrderData | null>(null);
  const [showAcquirePanel, setShowAcquirePanel] = useState(false);
  const [expectedDate, setExpectedDate] = useState<string>("");
  const [isSavingDate, setIsSavingDate] = useState(false);
  const [isUpdatingStatus, setIsUpdatingStatus] = useState(false);
  const [uploadingLr, setUploadingLr] = useState(false);
  const [lrUploaded, setLrUploaded] = useState(false);
  const [error, setError] = useState<string>("");
  const [designNames, setDesignNames] = useState<Record<string, string>>({});
  const [rmNames, setRmNames] = useState<Record<string, string>>({});
  const [showInvoicePanel, setShowInvoicePanel] = useState(false);
  const [showFullNotes, setShowFullNotes] = useState(false);
  const [showEditPanel, setShowEditPanel] = useState(false);

  // Confirm estimate / accept order straight from this page
  const [showConfirmPanel, setShowConfirmPanel] = useState(false);
  const [bulkAction, setBulkAction] = useState<BulkAction | null>(null);
  const [bulkIds, setBulkIds] = useState<number[]>([]);
  // Other open estimates / placed orders of the same client
  const [clientOpenOrders, setClientOpenOrders] = useState<ClientOpenOrder[]>([]);
  const [clientSelected, setClientSelected] = useState<Set<number>>(new Set());

  // Raw material view (no actions here)
  const [rawMaterials, setRawMaterials] = useState<AcquiredRawMaterial[]>([]);
  const [loadingRawMat, setLoadingRawMat] = useState(false);

  // Dropdown open/close
  const [actionsOpen, setActionsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const onDocClick = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setActionsOpen(false);
      }
    };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, []);

  const polishNote = (raw?: string) => {
    if (!raw) return "";
    let s = raw.replace(/\s+/g, " ").trim();
    if (!s) return "";
    s = s[0].toUpperCase() + s.slice(1);
    if (!/[.!?]"?$/.test(s)) s += ".";
    return s;
  };

  // ---- Roles from user_type (fallback to role_name) ----
  const rawType = (user?.user_type ?? user?.role_name ?? "")
    .toString()
    .toUpperCase();
  const isCustomer = rawType === "CUSTOMER";
  const isAdminOrEmployee = rawType === "ADMIN" || rawType === "EMPLOYEE";

  // ---- Load order details ----
  const fetchOrderData = async () => {
    setError("");
    if (!orderId || !user) return;

    const endpoint = isCustomer
      ? `/v1/door-management/saleorders/my-saleorders/${orderId}`
      : `/v1/door-management/saleorders/${orderId}`;

    try {
      const res = await WebService.getAPI<any>({ action: endpoint });
      if (res.status_code === 200 && res.data) {
        setOrderData(res.data);
        setDesignNames(res.data.design_names || {});
        setRmNames(res.data.rm_names || {});
        setExpectedDate(
          res.data.expected_delivery_date
            ? res.data.expected_delivery_date.slice(0, 10)
            : ""
        );
        setLrUploaded(!!res.data.lr_document);
      } else if (res.status_code === 401 || res.status_code === 403) {
        setError("Access denied. You do not have permission to view this order.");
        setOrderData(null);
        setLrUploaded(false);
      } else {
        setError("Could not load order details. Please try again.");
        setOrderData(null);
        setLrUploaded(false);
      }
    } catch (err: any) {
      setError(
        err?.response?.status === 401 || err?.response?.status === 403
          ? "Access denied. You do not have permission to view this order."
          : "Something went wrong while loading the order. Please try again."
      );
      setOrderData(null);
      setLrUploaded(false);
    }
  };

  useEffect(() => {
    fetchOrderData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderId, user]);

  // ---- Load acquired/derived raw materials (for the read-only right table)
const fetchRawMaterials = async () => {
  if (!orderData || !isAdminOrEmployee) return;
  if (!orderRequiresRawMaterialAcquisition(orderData)) {
    setRawMaterials([]);
    return;
  }
  setLoadingRawMat(true);
  try {
    const res = await WebService.getAPI<any>({
      action: `/v1/door-management/saleorders/acquire-raw-materials/${orderData.sale_order_id}`,
    });
    if (res?.status_code === 200 && Array.isArray(res.data)) {
      setRawMaterials(res.data);
    } else {
      setRawMaterials([]);
    }
  } catch {
    setRawMaterials([]);
  } finally {
    setLoadingRawMat(false);
  }
};


  const fetchClientOpenOrders = async () => {
    if (!orderData || !isAdminOrEmployee || !orderData.user_id) return;
    try {
      const [estimates, placed] = await Promise.all(
        ["CHECKOUT", "PLACED"].map((st) =>
          WebService.getAPI<any>({
            action: `/v1/door-management/saleorders?page_number=1&page_size=50&status=${st}&user_id=${orderData.user_id}`,
          }).catch(() => null)
        )
      );
      const rows: ClientOpenOrder[] = [
        ...(estimates?.data?.sale_orders || []),
        ...(placed?.data?.sale_orders || []),
      ].filter((o: ClientOpenOrder) => Number(o.sale_order_id) !== Number(orderData.sale_order_id));
      setClientOpenOrders(rows);
      setClientSelected(new Set());
    } catch {
      setClientOpenOrders([]);
    }
  };

  useEffect(() => {
    fetchClientOpenOrders();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderData?.sale_order_id, orderData?.status]);

  const openBulk = (action: BulkAction, ids: number[]) => {
    setBulkIds(ids);
    setBulkAction(action);
    setActionsOpen(false);
  };

  const handleRejectOrder = () => {
    if (!orderData) return;
    setActionsOpen(false);
    Swal.fire({
      title: "Reject this order?",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes, Reject",
      confirmButtonColor: "#FFD400",
      cancelButtonColor: "#eee",
    }).then((result) => {
      if (!result.isConfirmed) return;
      WebService.putAPI({
        action: `/v1/door-management/saleorders/accept-reject`,
        body: { sale_order_id: orderData.sale_order_id, status: "REJECTED", notes: "" },
      })
        .then(() => {
          toast.success("Order rejected.");
          fetchOrderData();
        })
        .catch(() => {});
    });
  };

  useEffect(() => {
    if (orderData && isAdminOrEmployee) fetchRawMaterials();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderData?.sale_order_id, rmNames]);

  const todayStr = new Date().toISOString().slice(0, 10);

  // PATCH expected delivery date
  const handleSaveExpectedDate = async () => {
    if (!expectedDate || !orderData) return;
    try {
      setIsSavingDate(true);
      const res: any = await WebService.patchAPI({
        action: `/v1/door-management/saleorders/${orderData.sale_order_id}`,
        body: { expected_delivery_date: expectedDate },
        isShowError: false,
      });
      const saved =
        res?.data?.expected_delivery_date ||
        res?.expected_delivery_date ||
        expectedDate;
      const savedDay = String(saved).slice(0, 10);
      setExpectedDate(savedDay);
      setOrderData((prev) =>
        prev ? { ...prev, expected_delivery_date: saved } : prev
      );
      toast.success(
        orderData.status === "PENDING" || orderData.status === "ACCEPTED"
          ? "Expected delivery date saved. Order moved to In Processing."
          : "Expected delivery date saved!"
      );
      await fetchOrderData();
    } catch (err: any) {
      const msg =
        (typeof err === "string" && err) ||
        err?.response?.data?.message ||
        "Failed to save expected delivery date.";
      toast.error(msg);
    } finally {
      setIsSavingDate(false);
    }
  };

  // ======== DOWNLOAD/VIEWS (all errors routed via WebService.errorHandler) ========
const handleViewInvoice = async () => {
  if (!orderData) return;
  if (!hasInvoiceDoc(orderData.invoice, orderData.invoice_id)) {
    toast.info("No invoice generated yet.");
    return;
  }
  try {
    await downloadInvoicePdf({
      invoiceId: invoiceIdOf(orderData.invoice, orderData.invoice_id),
      saleOrderId: orderData.sale_order_id,
      isCustomer,
      invoiceNumber: orderData.invoice?.invoice_number,
    });
    toast.success("Invoice downloaded.");
  } catch {
    // global handler already toasts
  }
};


  // View/Download Production Order
  const handleDownloadProductionOrder = async () => {
    if (!orderData) return;
    if (isCustomer) {
      toast.error("You don't have permission to view the Production Order.");
      return;
    }
    setActionsOpen(false);
    try {
      const blob = await WebService.downloadFileAPI({
        action: `/v1/door-management/saleorders/${orderData.sale_order_id}/production-order`,
        method: "GET",
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `Production_Order_${orderData.sale_order_id}_${timestamp}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("Production Order downloaded.");
      // Backend records the first generation; that unlocks the delivery date.
      setOrderData((prev) => (prev ? { ...prev, production_order_generated: true } : prev));
    } catch {
      // global handler shows error
    }
  };

  // Download Estimate (customer vs staff path)
  const handleDownloadEstimate = async () => {
    if (!orderData) return;
    try {
      const base = isCustomer
        ? "/v1/door-management/saleorders/my-saleorders"
        : "/v1/door-management/saleorders";
      const blob = await WebService.downloadFileAPI({
        action: `${base}/${orderData.sale_order_id}/estimations`,
        method: "GET",
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `Estimate_${orderData.sale_order_id}_${timestamp}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("Estimate downloaded.");
    } catch {
      /* handled globally */
    }
  };

  // Download Delivery Challan (staff)
  const handleDownloadDeliveryChallan = async () => {
    if (!orderData) return;
    if (!isAdminOrEmployee) return;
    try {
      const blob = await WebService.downloadFileAPI({
        action: `/v1/door-management/saleorders/${orderData.sale_order_id}/delivery-challan`,
        method: "GET",
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `Delivery_Challan_${orderData.sale_order_id}_${timestamp}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("Delivery Challan downloaded.");
    } catch {
      /* handled globally */
    }
  };

  const getWoodQualityName = (item: SaleOrderItem): string =>
    (item as any)?.wood_quality_name ||
    (item as any)?.wood_quality?.name ||
    (item as any)?.wood?.name ||
    "";

  // PATCH status transitions (with guards)
  const handleUpdateStatus = async (newStatus: string) => {
    if (!orderData) return;

    if (!isAdminOrEmployee) {
      toast.error("You don't have permission to update status.");
      return;
    }

    const statusNow = orderData.status; // uppercase
    const hasInvoiceNow = hasInvoiceDoc(orderData?.invoice, orderData?.invoice_id);

    if (newStatus === "SHIPPED") {
      if (statusNow !== "IN_PROCESSING" || !hasInvoiceNow || !expectedDate) {
        toast.error(
          "Cannot mark as shipped until invoice is generated and expected date is set."
        );
        return;
      }
    }

    setIsUpdatingStatus(true);
    try {
      await WebService.patchAPI({
        action: `/v1/door-management/saleorders/${orderData.sale_order_id}/status/${newStatus}`,
      });
      await Swal.fire({
        title: "Success!",
        text:
          newStatus === "SHIPPED"
            ? "Order marked as Shipped."
            : "Order marked as Completed.",
        icon: "success",
        confirmButtonColor: "#22C55E",
        heightAuto: false,
      });
      await fetchOrderData();
    } catch {
      Swal.fire({
        title: "Error!",
        text: "Status update failed.",
        icon: "error",
        confirmButtonColor: "#E13D22",
        heightAuto: false,
      });
    }
    setIsUpdatingStatus(false);
  };
const handleViewLR = async () => {
  if (!orderData) return;
  try {
    const res = await WebService.postAPI<any>({
      action: `/v1/door-management/saleorders/${orderData.sale_order_id}/view-lr`,
    });
    if (res?.status_code === 200 && res.data) {
      window.open(res.data, "_blank", "noopener,noreferrer");
    } else {
      toast.error("Unable to fetch LR link.");
    }
  } catch {
   
  }
};


  // ---- LR Upload logic (staff only) ----
  const handleUploadLR = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!orderData) return;
    if (!isAdminOrEmployee) {
      toast.error("You don't have permission to upload LR.");
      return;
    }

    const file = e.target.files?.[0];
    if (!file) return;

    const result = await Swal.fire({
      icon: "warning",
      title: "Upload LR and Complete Order",
      text: "Upload LR and Complete Sale Order",
      showCancelButton: true,
      confirmButtonText: "Yes, Upload & Complete",
      cancelButtonText: "Cancel",
      confirmButtonColor: "#10973b",
      cancelButtonColor: "#bababa",
      heightAuto: false,
    });
    if (!result.isConfirmed) return;

    setUploadingLr(true);
    const formData = new FormData();
    formData.append("image", file);

    try {
      // 1) Upload LR
      await WebService.postAPI({
        action: `/v1/door-management/saleorders/${orderData.sale_order_id}/upload-lr`,
        body: formData,
      });
      // 2) Mark as completed
      await handleUpdateStatus("COMPLETED");
      toast.success("LR uploaded & order marked as completed!");
      setLrUploaded(true);
      await fetchOrderData();
    } catch {
      toast.error("Failed to upload LR or complete order");
      setLrUploaded(false);
    } finally {
      setUploadingLr(false);
    }
  };
const GRID_ITEMS = "2.2fr 1fr .8fr .9fr .9fr .9fr 1fr 1.2fr 1.2fr 1fr";
// With Sr No column prefixed
const GRID_ITEMS_WITH_SR = ".5fr " + GRID_ITEMS;
  const getMicaName = (item: SaleOrderItem): string =>
    (item as any)?.mica_name || (item as any)?.mica?.mica_name || "";

  // --------- Derived UI flags ---------
  const status = orderData?.status || ""; // UPPERCASE
  const hasInvoice = hasInvoiceDoc(orderData?.invoice, orderData?.invoice_id);
  const dateUploaded = !!orderData?.expected_delivery_date;

  const disableAll =
    status === "COMPLETED" || status === "CANCELED" || status === "REJECTED";

  const awaitingDeliveryDate = status === "PENDING" || status === "ACCEPTED";
  // Production order is generated first; only then can the delivery date be set.
  const productionOrderDone = !awaitingDeliveryDate || !!orderData?.production_order_generated;

  const canEditDate =
    isAdminOrEmployee && (awaitingDeliveryDate || status === "IN_PROCESSING");

  const canShowDateInput =
    (isAdminOrEmployee &&
      ((awaitingDeliveryDate || status === "IN_PROCESSING") ||
        ((status === "SHIPPED" || status === "COMPLETED") && dateUploaded))) ||
    (isCustomer &&
      dateUploaded &&
      ["PENDING", "IN_PROCESSING", "SHIPPED", "COMPLETED"].includes(status));

  const dateInputDisabled =
    isCustomer ||
    (isAdminOrEmployee && (status === "SHIPPED" || status === "COMPLETED"));

  const requiresRawMaterialAcquisition =
    orderRequiresRawMaterialAcquisition(orderData);

  const canAcquireRaw =
    isAdminOrEmployee &&
    requiresRawMaterialAcquisition &&
    (status === "PENDING" || status === "IN_PROCESSING") &&
    dateUploaded;

  const showAcquireRaw =
    isAdminOrEmployee &&
    requiresRawMaterialAcquisition &&
    (status === "PENDING" || status === "IN_PROCESSING") &&
    dateUploaded;

  useEffect(() => {
    console.log("[SaleOrderDetail] gating", {
      isAdminOrEmployee,
      status,
      dateUploaded,
      canAcquireRaw,
      showAcquireRaw,
      showAcquirePanel,
    });
  }, [isAdminOrEmployee, status, dateUploaded, canAcquireRaw, showAcquireRaw, showAcquirePanel]);

  const canMarkShipped =
    isAdminOrEmployee && status === "IN_PROCESSING" && !!dateUploaded && hasInvoice;

  const canUploadLR = isAdminOrEmployee && status === "SHIPPED" && !lrUploaded;

  const canGenerateInvoice =
    isAdminOrEmployee && status === "IN_PROCESSING" && !hasInvoice;

  // Edit allowed from PENDING, IN_PROCESSING, and PLACED statuses, only if invoice is null
  const canEditOrder =
    isAdminOrEmployee && ["PENDING", "IN_PROCESSING", "PLACED"].includes(status) && !hasInvoice;

  // Estimate given to client -> confirm; confirmed (placed) -> accept / reject
  const canConfirmEstimate = isAdminOrEmployee && isEstimateStatus(status);
  const canAcceptOrder = isAdminOrEmployee && isPlacedStatus(status);

  // DROPDOWN item visibility (exact predicates preserved/extended)
  const canViewInvoiceBtn =
    hasInvoice && ["IN_PROCESSING", "SHIPPED", "COMPLETED"].includes(status);
  const canViewProductionOrderBtn =
    isAdminOrEmployee && (awaitingDeliveryDate || status === "IN_PROCESSING");
  const canDownloadDeliveryChallanBtn =
    isAdminOrEmployee && ["SHIPPED", "COMPLETED","IN_PROCESSING"].includes(status);
const canViewLRBtn = status === "COMPLETED";

  const progressStep = ORDER_STATUS_STEPS.findIndex((s) => s.key === status);
  const progressPercent = Math.max(
    0,
    ((progressStep + 1) / ORDER_STATUS_STEPS.length) * 100
  );

  const currency = (amt: number | undefined) =>
    typeof amt === "number"
      ? `₹${amt.toLocaleString("en-IN", { minimumFractionDigits: 2 })}`
      : "-";

  // ---- Error & loading handling ----
  if (error) {
    return (
      <div className={styles.loader} style={{ color: "#E13D22" }}>
        {error}
      </div>
    );
  }
  if (!orderData) {
    return <div className={styles.loader}>Loading...</div>;
  }

  return (
<div className={styles.detailWrapper}>
<div className={styles.headerRow}>
  <CapsuleTabs />
  <div className={styles.headerActions} ref={dropdownRef}>
    <button
      type="button"
      className={styles.actionsToggle}
      onClick={() => setActionsOpen(v => !v)}
      title="Actions"
    >
      <FaEllipsisV />
      Actions
    </button>

    {actionsOpen && (
      <div className={styles.actionsMenu}>
        {canConfirmEstimate && (
          <button
            className={styles.actionsItem}
            onClick={() => {
              setShowConfirmPanel(true);
              setActionsOpen(false);
            }}
            type="button"
            title="Client confirmed the estimate: record payment / credit and place the order"
          >
            Confirm Order (Payment)
          </button>
        )}
        {canConfirmEstimate && (
          <button
            className={styles.actionsItem}
            onClick={() => openBulk("CONFIRM_ESTIMATE", [orderData.sale_order_id])}
            type="button"
            title="Quick confirm on credit or full payment"
          >
            Quick Confirm Order
          </button>
        )}
        {canAcceptOrder && (
          <button
            className={styles.actionsItem}
            onClick={() => openBulk("ACCEPT", [orderData.sale_order_id])}
            type="button"
            title="Accept and assign to an employee"
          >
            Accept Order
          </button>
        )}
        {canAcceptOrder && (
          <button
            className={styles.actionsItem}
            onClick={handleRejectOrder}
            type="button"
            title="Reject Order"
          >
            Reject Order
          </button>
        )}
        {canEditOrder && (
          <button
            className={styles.actionsItem}
            onClick={() => {
              setShowEditPanel(true);
              setActionsOpen(false);
            }}
            type="button"
            title="Edit Order"
          >
            Edit Order
          </button>
        )}
        {canViewProductionOrderBtn && (
          <button
            className={styles.actionsItem}
            onClick={handleDownloadProductionOrder}
            type="button"
            title="View Production Order"
          >
            View Production Order
          </button>
        )}

        <button
          className={styles.actionsItem}
          onClick={handleDownloadEstimate}
          type="button"
          title="Download Estimate"
        >
          View Estimate
        </button>
        

        {canViewInvoiceBtn && (
          <>
           
            <button
              className={styles.actionsItem}
              onClick={handleViewInvoice}
              type="button"
              title="View Invoice"
            >
              View Invoice
            </button>
          </>
        )}

        {canDownloadDeliveryChallanBtn && (
          <>
            
            <button
              className={styles.actionsItem}
              onClick={handleDownloadDeliveryChallan}
              type="button"
              title="Download Delivery Challan"
            >
              View Delivery Challan
            </button>
            {canViewLRBtn && (
  <button
    className={styles.actionsItem}
    onClick={handleViewLR}
    type="button"
    title="View LR"
  >
    View LR
  </button>
)}
          </>
        )}
      </div>
    )}
  </div>
</div>



      {isAdminOrEmployee && (canConfirmEstimate || canAcceptOrder || awaitingDeliveryDate) && (
        <div
          style={{
            background: "#fffbe6",
            border: "1px solid #ffe08a",
            borderRadius: 10,
            padding: "10px 14px",
            margin: "10px 0",
            display: "flex",
            alignItems: "center",
            gap: 12,
            flexWrap: "wrap",
          }}
        >
          <span style={{ fontWeight: 600 }}>
            {canConfirmEstimate
              ? "Estimate shared with client. Once the client agrees, confirm the order."
              : canAcceptOrder
              ? "Order confirmed by client. Accept it and assign an employee."
              : !productionOrderDone
              ? "Order accepted. Generate the production order first, then set the expected delivery date."
              : "Production order generated. Enter the expected delivery date below to move it to production."}
          </span>
          {awaitingDeliveryDate && !canConfirmEstimate && !canAcceptOrder && (
            <button className={salesStyles.acceptBtn} onClick={handleDownloadProductionOrder}>
              {productionOrderDone ? "View Production Order" : "Generate Production Order"}
            </button>
          )}
          {canConfirmEstimate && (
            <button className={salesStyles.acceptBtn} onClick={() => setShowConfirmPanel(true)}>
              Confirm Order
            </button>
          )}
          {canAcceptOrder && (
            <>
              <button
                className={salesStyles.acceptBtn}
                onClick={() => openBulk("ACCEPT", [orderData.sale_order_id])}
              >
                Accept
              </button>
              <button className={salesStyles.rejectBtn} onClick={handleRejectOrder}>
                Reject
              </button>
            </>
          )}
        </div>
      )}

      {/* Other open orders of the same client: confirm them together */}
      {isAdminOrEmployee && clientOpenOrders.length > 0 && (() => {
        const selectedOthers = clientOpenOrders.filter((o) => clientSelected.has(Number(o.sale_order_id)));
        const thisId = Number(orderData.sale_order_id);
        const estimateIds = [
          ...(isEstimateStatus(status) ? [thisId] : []),
          ...selectedOthers.filter((o) => isEstimateStatus(o.status)).map((o) => Number(o.sale_order_id)),
        ];
        const placedIds = [
          ...(isPlacedStatus(status) ? [thisId] : []),
          ...selectedOthers.filter((o) => isPlacedStatus(o.status)).map((o) => Number(o.sale_order_id)),
        ];
        return (
          <div
            style={{
              border: "1px solid #eee",
              borderRadius: 10,
              padding: "10px 14px",
              margin: "10px 0",
              background: "#fafafa",
            }}
          >
            <div style={{ fontWeight: 700, marginBottom: 6 }}>
              This client has {clientOpenOrders.length} other open order(s)
            </div>
            {clientOpenOrders.map((o) => {
              const id = Number(o.sale_order_id);
              return (
                <label key={id} style={{ display: "flex", alignItems: "center", gap: 8, padding: "3px 0" }}>
                  <input
                    type="checkbox"
                    checked={clientSelected.has(id)}
                    onChange={() =>
                      setClientSelected((prev) => {
                        const next = new Set(prev);
                        if (next.has(id)) next.delete(id);
                        else next.add(id);
                        return next;
                      })
                    }
                  />
                  <span
                    style={{ color: "#1d4ed8", cursor: "pointer", textDecoration: "underline" }}
                    onClick={(e) => {
                      e.preventDefault();
                      navigate(`/saleorder/${id}`);
                    }}
                  >
                    #{id}
                  </span>
                  <span>{isEstimateStatus(o.status) ? "Estimate" : "Placed"}</span>
                  <span style={{ color: "#666" }}>
                    {currency(o.final_amount !== undefined ? Number(o.final_amount) : undefined)}
                  </span>
                  {o.created_at && (
                    <span style={{ color: "#999", fontSize: 13 }}>
                      {new Date(o.created_at).toLocaleDateString()}
                    </span>
                  )}
                </label>
              );
            })}
            <div style={{ display: "flex", gap: 8, marginTop: 8, flexWrap: "wrap" }}>
              {estimateIds.length > 0 && (
                <button
                  className={salesStyles.acceptBtn}
                  disabled={selectedOthers.length === 0}
                  onClick={() => openBulk("CONFIRM_ESTIMATE", estimateIds)}
                >
                  Confirm estimates ({estimateIds.length})
                </button>
              )}
              {placedIds.length > 0 && (
                <button
                  className={salesStyles.acceptBtn}
                  disabled={selectedOthers.length === 0}
                  onClick={() => openBulk("ACCEPT", placedIds)}
                >
                  Accept orders ({placedIds.length})
                </button>
              )}
            </div>
          </div>
        );
      })()}

      {showConfirmPanel &&
        ReactDOM.createPortal(
          <div className={salesStyles.overlay}>
            <div className={salesStyles.slideInPanelWrapper}>
              <AddSaleOrderPanel
                open={showConfirmPanel}
                onClose={() => {
                  setShowConfirmPanel(false);
                  fetchOrderData();
                }}
                saleOrderId={orderData.sale_order_id}
                userId={orderData.user_id}
                openedFromProceedToPay
              />
            </div>
          </div>,
          document.body
        )}

      {bulkAction && (
        <BulkConfirmModal
          action={bulkAction}
          saleOrderIds={bulkIds}
          onClose={() => setBulkAction(null)}
          onDone={() => {
            fetchOrderData();
            fetchClientOpenOrders();
          }}
        />
      )}

      {/* Acquire Raw Material Panel (staff + product items only) */}
      {isAdminOrEmployee && requiresRawMaterialAcquisition && (
        <AcquireRawMaterialPanel
          saleOrderId={orderData.sale_order_id}
          open={showAcquirePanel}
          onClose={() => setShowAcquirePanel(false)}
          onSuccess={() => {
            fetchOrderData();
            fetchRawMaterials();
          }}
        />
      )}

      {/* --- Order Info --- */}
      <div className={styles.orderBox}>
        <div className={styles.orderMetaSplit}>
          <div className={styles.orderMetaLeft}>
            <div>
              <strong>Order No:</strong> #{orderData.sale_order_id}
            </div>
            <div>
              <strong>Customer ID:</strong> CUST-{orderData.customer_id}
            </div>
            <div>
              <strong>Customer name:</strong> {orderData.username}
            </div>
          </div>

          <div className={styles.orderMetaRight}>
            <div>
              <strong>Order Date:</strong>{" "}
              {orderData.created_at
                ? new Date(orderData.created_at).toLocaleDateString()
                : "-"}
            </div>
            <div>
              <strong>Total Amount:</strong> {currency(orderData.final_amount)}
            </div>
            <div>
              <strong>Invoice:</strong>{" "}
              {orderData.invoice?.invoice_number
                ? orderData.invoice.invoice_number
                : hasInvoice
                ? `#${invoiceIdOf(orderData.invoice, orderData.invoice_id)}`
                : "Not generated"}
            </div>
            {(orderData.receipts?.length ?? 0) > 0 && (
              <div>
                <strong>Receipts:</strong> {orderData.receipts!.length}
              </div>
            )}
            {(() => {
              const cleaned = polishNote(orderData.notes);
              if (!cleaned) return null;

              const MAX_CHARS = 140;
              const isLong = cleaned.length > MAX_CHARS;
              const visible = showFullNotes
                ? cleaned
                : isLong
                ? cleaned.slice(0, MAX_CHARS) + "…"
                : cleaned;

              return (
                <div
                  className={styles.specialNoteCard}
                  style={{
                    marginTop: 10,
                    background: "#fafafa",
                    border: "1px solid #eee",
                    borderRadius: 10,
                    padding: "10px 12px",
                    boxShadow: "0 1px 4px rgba(0,0,0,0.03)",
                    maxWidth: 420,
                  }}
                  aria-label="Special request"
                >
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 8,
                      marginBottom: 6,
                    }}
                  >
                    <span role="img" aria-label="note">
                      📝
                    </span>
                    <div style={{ fontWeight: 700 }}>Special Request</div>
                    <div style={{ marginLeft: "auto", display: "flex", gap: 8 }}>
                      <button
                        type="button"
                        onClick={() => {
                          navigator.clipboard?.writeText(cleaned).then(
                            () => toast.success("Special request copied!"),
                            () => {}
                          );
                        }}
                        style={{
                          border: "none",
                          background: "transparent",
                          cursor: "pointer",
                          fontSize: 13,
                          textDecoration: "underline",
                        }}
                        title="Copy to clipboard"
                      >
                        Copy
                      </button>
                      {isLong && (
                        <button
                          type="button"
                          onClick={() => setShowFullNotes((v) => !v)}
                          style={{
                            border: "none",
                            background: "transparent",
                            cursor: "pointer",
                            fontSize: 13,
                            textDecoration: "underline",
                          }}
                          title={showFullNotes ? "Show less" : "Show more"}
                        >
                          {showFullNotes ? "Show less" : "Show more"}
                        </button>
                      )}
                    </div>
                  </div>
                  <div style={{ whiteSpace: "pre-wrap", lineHeight: 1.45 }}>
                    {visible}
                  </div>
                </div>
              );
            })()}
            {/* View LR link for everyone if it exists */}
            {orderData.lr_document && (
              <div style={{ marginTop: 8 }}>
                <a
                  href={mediaUrl(orderData.lr_document)}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{
                    background: "#fffbe7",
                    color: "#ba9500",
                    fontWeight: 600,
                    padding: "7px 20px",
                    borderRadius: 8,
                    textDecoration: "none",
                    fontSize: 15,
                    display: "inline-block",
                    boxShadow: "0 2px 8px #ffd40022",
                    marginTop: 6,
                  }}
                  title="View uploaded LR document"
                >
                  📄 View LR
                </a>
              </div>
            )}

            {/* Expected Date of Delivery */}
            {canShowDateInput && (
              <div className={styles.dateBlock}>
                <div className={styles.dateLabel}>Expected Date of Delivery</div>
                <div
                  style={{
                    display: "flex",
                    gap: 8,
                    alignItems: "center",
                    flexWrap: "wrap",
                  }}
                >
                  <input
                    type="date"
                    className={styles.dateInput}
                    value={expectedDate}
                    min={todayStr}
                    disabled={
                      dateInputDisabled ||
                      !productionOrderDone ||
                      isSavingDate ||
                      disableAll ||
                      status === "CANCELED" ||
                      status === "REJECTED"
                    }
                    onChange={(e) => setExpectedDate(e.target.value)}
                    style={{
                      background: dateInputDisabled ? "#f5f5f5" : undefined,
                      cursor: dateInputDisabled ? "not-allowed" : undefined,
                      fontWeight: dateInputDisabled ? 700 : 600,
                    }}
                  />
                  {/* Save button: staff only when editable */}
                  {canEditDate && (
                    <button
                      className={styles.acquireBtn}
                      onClick={handleSaveExpectedDate}
                      disabled={
                        !expectedDate ||
                        !productionOrderDone ||
                        isSavingDate ||
                        disableAll ||
                        expectedDate ===
                          (orderData.expected_delivery_date
                            ? orderData.expected_delivery_date.slice(0, 10)
                            : "")
                      }
                      style={{
                        minWidth: 90,
                        opacity: !expectedDate ? 0.6 : 1,
                        cursor: !expectedDate ? "not-allowed" : "pointer",
                      }}
                    >
                      {isSavingDate ? "Saving..." : "Save"}
                    </button>
                  )}
                  {isAdminOrEmployee && awaitingDeliveryDate && (
                    <span style={{ color: "#b45309", fontSize: 13, flexBasis: "100%" }}>
                      {productionOrderDone
                        ? "Saving the delivery date moves this order to In Processing (invoice)."
                        : "Generate the production order first to set the delivery date."}
                    </span>
                  )}

                  {/* Acquire Raw Material (staff only) */}
                  {showAcquireRaw && (
                    <button
                      className={styles.acquireBtn}
                      disabled={!canAcquireRaw || disableAll}
                      onClick={() => setShowAcquirePanel(true)}
                      style={{
                        background: "#10973b",
                        color: "#fff",
                        fontWeight: 700,
                        fontSize: 15,
                        borderRadius: 8,
                        padding: "10px 26px",
                        border: "none",
                        opacity: !canAcquireRaw ? 0.5 : 1,
                        cursor: !canAcquireRaw ? "not-allowed" : "pointer",
                      }}
                    >
                      Acquire Raw Material
                    </button>
                  )}

                  {/* Mark as Shipped (staff only; requires invoice + expected date) */}
                  {canMarkShipped && (
                    <button
                      className={styles.statusActionBtn}
                      onClick={() => handleUpdateStatus("SHIPPED")}
                      disabled={isUpdatingStatus}
                      style={{
                        background: "#10973b",
                        color: "#fff",
                        minWidth: 160,
                        fontWeight: 700,
                        border: "none",
                      }}
                    >
                      {isUpdatingStatus ? "Updating..." : "Mark as Shipped"}
                    </button>
                  )}
                </div>
              </div>
            )}

            {/* ---- Action Buttons Row ---- */}
            <div className={styles.actionButtons}>
              {/* Upload LR (staff only; SHIPPED & not uploaded) */}
              {canUploadLR && (
                <label
                  className={`${styles.acquireBtn} ${styles.lrUploadLabel}`}
                  style={{ cursor: "pointer" }}
                >
                  <FaFileUpload style={{ marginRight: 6 }} />
                  {uploadingLr ? "Uploading..." : "Upload LR"}
                  <input
                    type="file"
                    accept="application/pdf,image/*"
                    style={{ display: "none" }}
                    disabled={uploadingLr}
                    onChange={handleUploadLR}
                  />
                </label>
              )}

              {/* Generate Invoice (staff only; IN_PROCESSING & no invoice) */}
              {canGenerateInvoice && (
                <button
                  className={styles.statusActionBtn}
                  style={{
                    background: "#FFD400",
                    color: "#111",
                    minWidth: 160,
                    fontWeight: 700,
                    border: "none",
                  }}
                  onClick={() => setShowInvoicePanel(true)}
                  type="button"
                >
                  🧾 Generate Invoice
                </button>
              )}

             

              {/* ===== LEGACY BUTTONS HIDDEN (kept to preserve logic/lines) ===== */}
              {SHOW_LEGACY_ACTION_BTNS && isAdminOrEmployee && status === "IN_PROCESSING" && (
                <button
                  className={styles.statusActionBtn}
                  style={{
                    background: "#111",
                    color: "#fff",
                    minWidth: 200,
                    fontWeight: 700,
                    border: "none",
                  }}
                  onClick={handleDownloadProductionOrder}
                  type="button"
                  title="Download Production Order PDF"
                >
                  View Production Order
                </button>
              )}

              {SHOW_LEGACY_ACTION_BTNS &&
                hasInvoice &&
                ["IN_PROCESSING", "SHIPPED", "COMPLETED"].includes(status) && (
                  <button
                    className={styles.statusActionBtn}
                    style={{
                      background: "#111",
                      color: "#fff",
                      minWidth: 140,
                      fontWeight: 700,
                      border: "none",
                    }}
                    onClick={handleViewInvoice}
                    type="button"
                  >
                    View Invoice
                  </button>
                )}

              {status === "SHIPPED" && lrUploaded && (
                <span className={styles.lrUploadedBadge}>
                  <FaCheckCircle style={{ marginRight: 4 }} />
                  LR Uploaded
                </span>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* ---- Invoice & Receipts ---- */}
      <div className={styles.orderBox} style={{ marginTop: 20 }}>
        <div style={{ fontWeight: 700, fontSize: 18, marginBottom: 14, color: "#111827" }}>
          Invoice & Receipts
        </div>
        <BillingDocuments
          saleOrderId={orderData.sale_order_id}
          invoice={orderData.invoice}
          invoiceId={orderData.invoice_id}
          receipts={orderData.receipts}
          isCustomer={isCustomer}
        />
      </div>

      {/* ---- LIVE PROGRESS BAR ---- */}
      <div className={styles.progressStepper}>
        <div className={styles.progressTrack}>
          <div
            className={styles.progressBar}
            style={{ width: `${progressPercent}%`, background: "#10973b" }}
          />
        </div>
        <div className={styles.progressStepsRow}>
          {ORDER_STATUS_STEPS.map((step, idx) => (
            <div
              key={step.key}
              className={`${styles.progressStepDot} ${
                idx <= progressStep ? styles.activeDot : ""
              }`}
              title={step.label}
            >
              <span className={styles.progressStepIcon}>
                {STEP_ICONS[step.key]}
              </span>
              <div className={styles.progressStepLabel}>{step.label}</div>
            </div>
          ))}
        </div>
      </div>
      {showInvoicePanel && orderData && (
        <GenerateInvoicePanel
          open={showInvoicePanel}
          orderId={orderData.sale_order_id}
          isCustomer={isCustomer}
          onClose={() => setShowInvoicePanel(false)}
          onSuccess={fetchOrderData}
          onOpenAcquireRawMaterial={() => setShowAcquirePanel(true)}
          requiresRawMaterialAcquisition={requiresRawMaterialAcquisition}
        />
      )}
      {showEditPanel && orderData && (
        <EditSaleOrderPanel
          open={showEditPanel}
          order={orderData}
          onClose={() => setShowEditPanel(false)}
          onUpdated={() => {
            setShowEditPanel(false);
            fetchOrderData();
          }}
          onChanged={fetchOrderData}
        />
      )}
      <div
        style={{
          display: "grid",
          gridTemplateColumns:
            isAdminOrEmployee && requiresRawMaterialAcquisition
              ? "2.2fr 0.8fr"
              : "1fr",
          gap: 18,
        }}
      >
        <div className={styles.orderBox}>
          <h3>Order Items</h3>
          <div
            className={`${styles.itemTableGrid} ${styles.header}`}
          style={{ gridTemplateColumns: GRID_ITEMS_WITH_SR, fontSize: 13 }}
          >
            <div>Sr No</div>
            <div>Name</div>
            <div>Item</div>
            <div>Quantity</div>
            <div>Sagwan B</div>
            <div>Double D</div>
            <div>Back Design</div>
            <div>B-PVC</div>
            <div>Height (Margin)</div>
            <div>Width (Margin)</div>
            <div>Mica Name</div>
          </div>

          {(!orderData.sale_order_items || orderData.sale_order_items.length === 0) ? (
            <div className={styles.loader} style={{ color: "#aaa" }}>
              No items found for this order.
            </div>
          ) : (
            orderData.sale_order_items.map((item, idx) => {
              const isRM = (item.product_type || "").toUpperCase() === "RAW_MATERIAL";

              // Prefer the ordered item's own name from the API; maps are fallback only.
              const baseName =
                item.product_name ||
                item.name ||
                item.product?.name ||
                (isRM
                  ? rmNames?.[String(item.product_id)]
                  : designNames?.[String(item.product_id)]) ||
                (isRM ? "Raw Material" : "Product");

              const woodQ = (getWoodQualityName(item) || "").trim();
              const showWoodQInName =
                !isRM && woodQ && woodQ.toLowerCase() !== "unknown";
              const displayName = showWoodQInName ? `${baseName} (${woodQ})` : baseName;

              const yesNo = (v?: boolean) =>
                typeof v === "boolean" ? (v ? "Yes" : "No") : "—";

              // Size with margin merged, e.g. 84 + 0.5 -> "84.5"
              const fmtDimProduct = (n?: number, m?: number) =>
                typeof n === "number" && n !== 0
                  ? String(Math.round((n + (typeof m === "number" ? m : 0)) * 100) / 100)
                  : "—";

              const rawDim = (n?: number) =>
                typeof n === "number" && n > 0 ? String(n) : "—";

              const mica = getMicaName(item);

              return (
                <div
                  key={item.sale_order_items_id}
                  className={`${styles.itemTableGrid} ${styles.row}`}
                  style={{
                    gridTemplateColumns: GRID_ITEMS_WITH_SR,
                    fontSize: 13,
                  }}
                >
                  <div>{idx + 1}</div>
                  <div>
                    {displayName}
                    {!!Number(item.additional_charge) && (
                      <div style={{ fontSize: 12, color: Number(item.additional_charge) < 0 ? "#1e8542" : "#b45309" }}>
                        {item.additional_charge_note || "Other charges"}:{" "}
                        {Number(item.additional_charge) < 0 ? "−" : "+"}₹
                        {Math.abs(Number(item.additional_charge)).toLocaleString("en-IN")}
                      </div>
                    )}
                  </div>
                  <div>{isRM ? "Raw Material" : "Product"}</div>
                  <div>{typeof item.quantity === "number" ? item.quantity : "—"}</div>
                  <div>{isRM ? "—" : yesNo(item.is_sagwan_bidding)}</div>
                  <div>{isRM ? "—" : yesNo(item.door_config)}</div>
                  <div>{isRM ? "—" : yesNo(item.is_back_design)}</div>
                  <div>{isRM ? "—" : yesNo(item.is_back_puc)}</div>
                  <div>
                    {isRM
                      ? rawDim(item.height)
                      : fmtDimProduct(item.height, item.height_margin)}
                  </div>
                  <div>
                    {isRM
                      ? rawDim(item.width)
                      : fmtDimProduct(item.width, item.width_margin)}
                  </div>
                  <div>{isRM ? "—" : mica || "—"}</div>
                </div>
              );
            })
          )}

          {Array.isArray((orderData as any).other_charges) && (orderData as any).other_charges.length > 0 && (
            <div style={{ marginTop: 12, fontSize: 13 }}>
              <div style={{ fontWeight: 700, marginBottom: 4 }}>Other Charges</div>
              {(orderData as any).other_charges.map((c: { description: string; amount: number }, i: number) => (
                <div key={i} style={{ display: "flex", justifyContent: "space-between", maxWidth: 420 }}>
                  <span>{c.description}</span>
                  <span style={{ color: Number(c.amount) < 0 ? "#1e8542" : "#b45309", fontWeight: 600 }}>
                    {Number(c.amount) < 0 ? "−" : "+"}₹{Math.abs(Number(c.amount)).toLocaleString("en-IN")}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* RIGHT: Acquired production raw materials (products only) */}
        {isAdminOrEmployee && requiresRawMaterialAcquisition && (
          <div className={styles.orderBox}>
            <h3>Raw Materials</h3>
            <div
              className={`${styles.itemTableGrid} ${styles.header}`}
              style={{ gridTemplateColumns: ".5fr 2fr .8fr", fontSize: 13 }}
            >
              <div>Sr No</div>
              <div>Raw Material</div>
              <div>Qty</div>
            </div>

            {loadingRawMat ? (
              <div className={styles.loader}>Loading...</div>
            ) : rawMaterials.length === 0 ? (
              <div className={styles.loader} style={{ color: "#777" }}>
                No Raw Materials.
              </div>
            ) : (
              rawMaterials.map((rm, idx) => (
                <div
                  key={rm.acquisition_id}
                  className={`${styles.itemTableGrid} ${styles.row}`}
                  style={{ gridTemplateColumns: ".5fr 2fr .8fr", fontSize: 13 }}
                >
                  <div>{idx + 1}</div>
                  <div>{rm.raw_material_name}</div>
                  <div>{rm.quantity}</div>
                </div>
              ))
            )}
          </div>
        )}
      </div>
    </div>
  );
};
export default SaleOrderDetail;
